import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { domainService } from '../../services/domainService';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface SubdomainSettingsProps {
  currentSubdomain: string | null;
  baseDomain?: string;
  onSubdomainUpdated: () => void;
}

export const SubdomainSettings: React.FC<SubdomainSettingsProps> = ({
  currentSubdomain,
  baseDomain = 'kynection.com',
  onSubdomainUpdated,
}) => {
  const { toast } = useToast();
  const [subdomain, setSubdomain] = useState(currentSubdomain || '');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{
    checked: boolean;
    available: boolean;
    error?: string;
    suggested?: string;
  } | null>(null);

  // Reset availability status when subdomain changes
  useEffect(() => {
    if (subdomain !== currentSubdomain) {
      setAvailabilityStatus(null);
    }
  }, [subdomain, currentSubdomain]);

  // Validate subdomain format client-side
  const validateFormat = (value: string): string | null => {
    if (value.length < 3) return 'Subdomain must be at least 3 characters';
    if (value.length > 63) return 'Subdomain must be at most 63 characters';
    if (!/^[a-z0-9-]+$/.test(value)) {
      return 'Only lowercase letters, numbers, and hyphens allowed';
    }
    if (value.startsWith('-')) return 'Cannot start with a hyphen';
    if (value.endsWith('-')) return 'Cannot end with a hyphen';
    if (value.includes('--')) return 'Cannot contain consecutive hyphens';
    return null;
  };

  const handleSubdomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().trim();
    setSubdomain(value);
  };

  const handleCheckAvailability = async () => {
    const formatError = validateFormat(subdomain);
    if (formatError) {
      setAvailabilityStatus({
        checked: true,
        available: false,
        error: formatError,
      });
      return;
    }

    setIsChecking(true);
    try {
      const result = await domainService.checkSubdomainAvailability(subdomain);
      setAvailabilityStatus({
        checked: true,
        available: result.available,
        error: result.error,
        suggested: result.suggested,
      });

      if (result.available) {
        toast({
          title: 'Available!',
          description: `${subdomain}.${baseDomain} is available`,
        });
      } else if (result.suggested) {
        toast({
          title: 'Not Available',
          description: `Try: ${result.suggested}`,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to check availability',
        variant: 'destructive',
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleSave = async () => {
    if (!availabilityStatus?.available) {
      toast({
        title: 'Cannot Save',
        description: 'Please check subdomain availability first',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      await domainService.updateSubdomain(subdomain);
      toast({
        title: 'Success!',
        description: 'Subdomain updated successfully',
      });
      onSubdomainUpdated();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update subdomain',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const useSuggested = () => {
    if (availabilityStatus?.suggested) {
      setSubdomain(availabilityStatus.suggested);
      setAvailabilityStatus(null);
    }
  };

  const isUnchanged = subdomain === (currentSubdomain || '');
  const formatError = validateFormat(subdomain);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subdomain</CardTitle>
        <CardDescription>
          Set your workspace subdomain. This will be the URL your team uses to access the platform.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Your Subdomain</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 border rounded-md pr-3">
              <Input
                value={subdomain}
                onChange={handleSubdomainChange}
                placeholder="acme"
                className="border-0 focus-visible:ring-0"
                disabled={isSaving}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                .{baseDomain}
              </span>
            </div>
            <Button
              onClick={handleCheckAvailability}
              disabled={isChecking || isUnchanged || !!formatError}
              variant="outline"
            >
              {isChecking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking
                </>
              ) : (
                'Check'
              )}
            </Button>
          </div>

          {/* Validation Messages */}
          {formatError && !availabilityStatus && (
            <div className="flex items-center gap-2 text-sm text-yellow-600">
              <AlertCircle className="h-4 w-4" />
              <span>{formatError}</span>
            </div>
          )}

          {/* Availability Status */}
          {availabilityStatus?.checked && (
            <div className="space-y-2">
              {availabilityStatus.available ? (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>{subdomain}.{baseDomain} is available!</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <XCircle className="h-4 w-4" />
                    <span>{availabilityStatus.error}</span>
                  </div>
                  {availabilityStatus.suggested && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Suggestion:</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={useSuggested}
                      >
                        {availabilityStatus.suggested}.{baseDomain}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Current Subdomain Display */}
        {!isUnchanged && currentSubdomain && (
          <div className="text-xs text-muted-foreground bg-muted p-3 rounded">
            <p>
              Current subdomain: <code className="font-mono">{currentSubdomain}.{baseDomain}</code>
            </p>
          </div>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isUnchanged || !availabilityStatus?.available || isSaving}
          className="w-full"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Update Subdomain'
          )}
        </Button>

        {/* Info Box */}
        <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded border border-blue-200 dark:border-blue-800">
          <p className="font-medium mb-1">📋 Subdomain Rules:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>3-63 characters long</li>
            <li>Lowercase letters, numbers, and hyphens only</li>
            <li>Cannot start or end with a hyphen</li>
            <li>Cannot contain consecutive hyphens</li>
            <li>Cannot use reserved names (app, api, admin, etc.)</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
